import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Welcome from "@/pages/Welcome";
import Dashboard from "@/pages/Dashboard";
import Chapter from "@/pages/Chapter";
import Story from "@/pages/Story";
import Reply from "@/pages/Reply";
import Privacy from "@/pages/Privacy";
import AdminEmails from "@/pages/AdminEmails";
import AdminHome from "@/pages/AdminHome";
import ForgotPassword from "@/pages/ForgotPassword";
import KeepsakeBuilder from "@/pages/KeepsakeBuilder";
import StoryBuilder from "@/pages/StoryBuilder";
import ResetPassword from "@/pages/ResetPassword";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import GiftPage from "@/pages/Gift";
import TwoStep from "@/pages/TwoStep";
import MfaRecovery from "@/pages/MfaRecovery";
import AdminUsers from "@/pages/AdminUsers";
import AccountSecurity from "@/pages/AccountSecurity";
import { useGiftsEnabled } from "@/hooks/useGiftsEnabled";
import GiftSuccess from "@/pages/GiftSuccess";
import Redeem from "@/pages/Redeem";

function AdminOnly({ children }) {
  const { user } = useAuth();
  return user.role === "admin" ? children : <Navigate to="/dashboard" replace />;
}

function OwnerOnly({ children }) {
  const { user } = useAuth();
  return user.role === "admin" && !user.readonly ? children : <Navigate to="/admin" replace />;
}

function GiftGate({ children }) {
  const on = useGiftsEnabled();
  if (on === null) return <div className="min-h-screen legacy-bg" />;
  return on ? children : <Navigate to="/" replace />;
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen legacy-bg" />;
  if (!user) return <Navigate to="/signin?mode=login" replace />;
  if (!user.paid) return <Navigate to="/signin" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/signin" element={<Auth />} />
          <Route path="/reply/:token" element={<Reply />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/gift" element={<GiftGate><GiftPage /></GiftGate>} />
          <Route path="/gift/success" element={<GiftSuccess />} />
          <Route path="/redeem" element={<GiftGate><Redeem /></GiftGate>} />
          <Route path="/welcome" element={<Protected><Welcome /></Protected>} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/member/:id" element={<Protected><Chapter /></Protected>} />
          <Route path="/keepsake-builder" element={<Protected><KeepsakeBuilder /></Protected>} />
          <Route path="/story-builder" element={<Protected><StoryBuilder /></Protected>} />
          <Route path="/assemble" element={<Navigate to="/keepsake-builder" replace />} />
          <Route path="/admin" element={<Protected><AdminOnly><AdminHome /></AdminOnly></Protected>} />
          <Route path="/admin/emails" element={<Protected><AdminOnly><AdminEmails /></AdminOnly></Protected>} />
          <Route path="/admin/users" element={<Protected><AdminOnly><OwnerOnly><AdminUsers /></OwnerOnly></AdminOnly></Protected>} />
          <Route path="/admin/security" element={<Protected><AdminOnly><AccountSecurity /></AdminOnly></Protected>} />
          <Route path="/two-step" element={<TwoStep />} />
          <Route path="/two-step/recovery" element={<MfaRecovery />} />
          <Route path="/story/:kind" element={<Protected><Story /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" theme="dark" richColors />
    </AuthProvider>
  );
}

export default App;
